import { c as _c } from "react/compiler-runtime";
import { useLayoutEffect } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { trackRollQuantityReset, trackRollQuantitySelected } from "@/lib/analytics/segment";
import { ROLL_QUANTITY_ERROR } from "@/lib/product/validation";
import { cn } from "@/lib/utils";
import { useProduct } from "@/queries/useProduct";
import { useSelectedQuantityPrice } from "@/queries/useSelectedQuantityPrice";
import { usePdpStore } from "@/stores/usePdpStore";
import { PackagingMode } from "@/utils/resolvePackagingMode";
import { buildRollOptions, carryOverRollSelection, FEWEST_ROLLS_LABEL, FEWEST_ROLLS_SELECTION, FEWEST_ROLLS_VALUE, offersFewestRolls, rollSelectValue, selectionFromOption, toRollOptionValue } from "@/utils/rollQuantityUtils";
import PackagingQuantityOption, { packagingQuantitySelectItemClass, packagingQuantitySelectTriggerClass } from "./PackagingQuantityOption";
import { useProductBuilderContext } from "./ProductBuilderContext";
import { useFieldErrorId } from "./ValidationError";
interface Props {
  labelId?: string;
}
export default function RollQuantity(t0) {
  const $ = _c(53);
  const {
    labelId
  } = t0;
  const {
    product
  } = useProductBuilderContext();
  const {
    data: productAdmin
  } = useProduct(product.sku);
  const {
    quantityPrice,
    isTrusted
  } = useSelectedQuantityPrice(productAdmin);
  const selectedQuantity = usePdpStore(_temp);
  const rollDriver = usePdpStore(_temp2);
  const rollCount = usePdpStore(_temp3);
  const rollQty = usePdpStore(_temp4);
  const setRollSelection = usePdpStore(_temp5);
  const setValidationError = usePdpStore(_temp6);
  const clearValidationError = usePdpStore(_temp7);
  const rollQuantityErrorId = useFieldErrorId("rollQuantity");
  const t1 = quantityPrice?.rollLayout;
  let t2;
  if ($[0] !== rollDriver || $[1] !== t1) {
    t2 = buildRollOptions(t1, rollDriver);
    $[0] = rollDriver;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const rollOptions = t2;
  let t3;
  let t4;
  if ($[3] !== isTrusted || $[4] !== rollCount || $[5] !== rollDriver || $[6] !== rollOptions || $[7] !== rollQty || $[8] !== setRollSelection || $[9] !== setValidationError) {
    t3 = () => {
      if (!isTrusted || rollCount === null && rollQty === null) {
        return;
      }
      const next = carryOverRollSelection(rollOptions, rollDriver, {
        rollCount,
        rollQty
      });
      if (next.rollCount === rollCount && next.rollQty === rollQty) {
        return;
      }
      setRollSelection({
        rollDriver,
        ...next
      });
      if (next.rollCount === null) {
        setValidationError("rollQuantity", ROLL_QUANTITY_ERROR);
        trackRollQuantityReset();
      }
    };
    t4 = [isTrusted, rollOptions, rollDriver, rollCount, rollQty, setRollSelection, setValidationError];
    $[3] = isTrusted;
    $[4] = rollCount;
    $[5] = rollDriver;
    $[6] = rollOptions;
    $[7] = rollQty;
    $[8] = setRollSelection;
    $[9] = setValidationError;
    $[10] = t3;
    $[11] = t4;
  } else {
    t3 = $[10];
    t4 = $[11];
  }
  useLayoutEffect(t3, t4);
  const quantityControlId = `roll-quantity-select-${product.sku}`;
  let t5;
  if ($[12] !== rollCount || $[13] !== rollOptions || $[14] !== rollQty) {
    let t6;
    if ($[16] !== rollCount || $[17] !== rollQty) {
      t6 = option => option.numRolls === rollCount && option.itemsPerRoll === rollQty;
      $[16] = rollCount;
      $[17] = rollQty;
      $[18] = t6;
    } else {
      t6 = $[18];
    }
    t5 = rollOptions.some(t6);
    $[12] = rollCount;
    $[13] = rollOptions;
    $[14] = rollQty;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const isSelected = t5;
  let t6;
  if ($[19] !== clearValidationError || $[20] !== rollDriver || $[21] !== rollOptions || $[22] !== setRollSelection) {
    t6 = function handleValueChange(value) {
      clearValidationError("rollQuantity");
      if (value === FEWEST_ROLLS_VALUE) {
        setRollSelection(FEWEST_ROLLS_SELECTION);
        trackRollQuantitySelected({
          numRolls: FEWEST_ROLLS_SELECTION.rollCount,
          itemsPerRoll: null
        });
        return;
      }
      const selectedOption = rollOptions.find(option_0 => toRollOptionValue(option_0.numRolls, option_0.itemsPerRoll) === value);
      if (!selectedOption) {
        return;
      }
      setRollSelection(selectionFromOption(rollDriver, {
        rollCount: selectedOption.numRolls,
        rollQty: selectedOption.itemsPerRoll
      }));
      trackRollQuantitySelected(selectedOption);
    };
    $[19] = clearValidationError;
    $[20] = rollDriver;
    $[21] = rollOptions;
    $[22] = setRollSelection;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  const handleValueChange = t6;
  let t7;
  if ($[24] !== isSelected || $[25] !== rollCount || $[26] !== rollDriver || $[27] !== rollQty) {
    t7 = rollSelectValue(rollDriver, isSelected ? {
      rollCount,
      rollQty
    } : null);
    $[24] = isSelected;
    $[25] = rollCount;
    $[26] = rollDriver;
    $[27] = rollQty;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  const t8 = selectedQuantity === undefined;
  const t9 = rollQuantityErrorId ? true : undefined;
  const t10 = `${quantityControlId}-content`;
  let t11;
  let t12;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = cn("w-full", packagingQuantitySelectTriggerClass);
    t12 = <SelectValue placeholder="-- Select Roll Quantity --" />;
    $[29] = t11;
    $[30] = t12;
  } else {
    t11 = $[29];
    t12 = $[30];
  }
  let t13;
  if ($[31] !== labelId || $[32] !== rollQuantityErrorId || $[33] !== t10 || $[34] !== t9) {
    t13 = <SelectTrigger aria-labelledby={labelId} aria-describedby={rollQuantityErrorId} aria-invalid={t9} aria-controls={t10} className={t11}>{t12}</SelectTrigger>;
    $[31] = labelId;
    $[32] = rollQuantityErrorId;
    $[33] = t10;
    $[34] = t9;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  const t14 = `${quantityControlId}-content`;
  let t15;
  if ($[36] !== rollDriver) {
    t15 = offersFewestRolls(rollDriver) && <SelectItem value={FEWEST_ROLLS_VALUE}>{FEWEST_ROLLS_LABEL}</SelectItem>;
    $[36] = rollDriver;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== rollDriver || $[39] !== rollOptions) {
    let t17;
    if ($[41] !== rollDriver) {
      t17 = option_1 => <SelectItem key={toRollOptionValue(option_1.numRolls, option_1.itemsPerRoll)} value={toRollOptionValue(option_1.numRolls, option_1.itemsPerRoll)} className={packagingQuantitySelectItemClass}><PackagingQuantityOption packageCount={option_1.numRolls} quantityPerPackage={option_1.itemsPerRoll} packagingMode={PackagingMode.Roll} rollDriver={rollDriver} /></SelectItem>;
      $[41] = rollDriver;
      $[42] = t17;
    } else {
      t17 = $[42];
    }
    t16 = rollOptions.map(t17);
    $[38] = rollDriver;
    $[39] = rollOptions;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[43] !== t14 || $[44] !== t15 || $[45] !== t16) {
    t17 = <SelectContent id={t14} className="min-w-80">{t15}{t16}</SelectContent>;
    $[43] = t14;
    $[44] = t15;
    $[45] = t16;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  let t18;
  if ($[47] !== handleValueChange || $[48] !== t13 || $[49] !== t17 || $[50] !== t7 || $[51] !== t8) {
    t18 = <Select value={t7} onValueChange={handleValueChange} disabled={t8}>{t13}{t17}</Select>;
    $[47] = handleValueChange;
    $[48] = t13;
    $[49] = t17;
    $[50] = t7;
    $[51] = t8;
    $[52] = t18;
  } else {
    t18 = $[52];
  }
  return t18;
}
function _temp7(state_5) {
  return state_5.clearValidationError;
}
function _temp6(state_4) {
  return state_4.setValidationError;
}
function _temp5(state_3) {
  return state_3.setRollSelection;
}
function _temp4(state_2) {
  return state_2.rollQty;
}
function _temp3(state_1) {
  return state_1.rollCount;
}
function _temp2(state_0) {
  return state_0.rollDriver;
}
function _temp(state) {
  return state.quantity;
}